/**
 * Reads the `intervals` payload `zunox-agent` sends since adopting the Zunox
 * V2 capture core: closed intervals in the interval contract's own JSON form
 * (schema version 3). They are converted to the `ActivitySummary` shape the
 * activity views already aggregate, with each event carrying its real start
 * time, because intervals are not contiguous: time the agent did not observe
 * belongs to no interval and must stay empty rather than be back-filled.
 *
 * Older agents send `kind: "activity"` payloads, which pass through as-is.
 */
import { cleanText } from '~/util/cleanText';
/** Shown in place of a title that was deliberately withheld. */
export const PRIVATE_WINDOW_TITLE = 'Private window';
/** Shown for an event whose tracker recorded no title at all. */
export const NO_TITLE = '(no window title recorded)';
/**
 * One contract interval as an activity event. Self reads (the agent's own
 * windows) are dropped: the contract stores them but they are never foreign
 * capture. `unknown` presence counts as not idle, since the contract makes
 * no claim either way and idle is the stronger statement.
 */
function toEvent(interval) {
    if (!interval.is_foreign_capture)
        return null;
    const startMs = Date.parse(interval.start_utc);
    if (Number.isNaN(startMs) || !(interval.duration_seconds > 0))
        return null;
    return {
        afk: interval.active_state === 'idle',
        // An app with no name (none, or only spaces or invisible characters)
        // goes by its bundle id, here and in the engine.
        app: cleanText(interval.app_name) ? interval.app_name : interval.bundle_id,
        // Neither a name nor a bundle id: the engine keys it as no name.
        ...(!cleanText(interval.app_name) && !cleanText(interval.bundle_id)
            ? { recorded_app: '' }
            : {}),
        bundle_id: interval.bundle_id,
        domain: interval.browser_domain ?? null,
        private_browsing: interval.browser_privacy === 'private_browsing',
        title: interval.window_title ?? (interval.title_redacted ? PRIVATE_WINDOW_TITLE : null),
        duration_secs: interval.duration_seconds,
        start: startMs / 1000,
        interval_id: interval.id ?? undefined,
        keyboard_active_seconds: interval.keyboard_active_seconds,
        mouse_active_seconds: interval.mouse_active_seconds,
        mouse_without_keyboard_seconds: interval.mouse_without_keyboard_seconds,
    };
}
export function isIntervalsPayload(value) {
    return (typeof value === 'object' &&
        value !== null &&
        value.kind === 'intervals' &&
        Array.isArray(value.intervals));
}
/** Any decrypted payload, in the shape the activity views aggregate. */
export function normalizeActivityPayload(value) {
    if (!isIntervalsPayload(value)) {
        // Legacy `activity` payloads pass through. Anything else (an unknown or
        // future payload kind) contributes no events rather than breaking every
        // page that iterates them.
        const legacy = value;
        if (legacy && Array.isArray(legacy.events))
            return legacy;
        return {
            kind: String(legacy?.kind ?? 'unknown'),
            window_start: Number(legacy?.window_start ?? 0),
            window_end: Number(legacy?.window_end ?? 0),
            events: [],
        };
    }
    const events = value.intervals
        .map(toEvent)
        .filter((e) => e !== null)
        .sort((a, b) => (a.start ?? 0) - (b.start ?? 0));
    return {
        kind: 'intervals',
        window_start: value.window_start,
        window_end: value.window_end,
        events,
        capture: value.capture,
    };
}
/**
 * Drops events whose interval id was already seen in an earlier payload.
 * The agent can resend a batch if it crashes between a successful send and
 * saving its upload cursor; interval ids are unique per device, so callers
 * pass one device's payloads at a time.
 */
export function dedupeIntervals(payloads) {
    const seen = new Set();
    return payloads.map(payload => ({
        ...payload,
        events: payload.events.filter(event => {
            if (event.interval_id === undefined)
                return true;
            if (seen.has(event.interval_id))
                return false;
            seen.add(event.interval_id);
            return true;
        }),
    }));
}
